import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import ReadAloudButton from "@/components/ReadAloudButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;

function applyInlineBold(text) {
  return text.replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>");
}

function renderSegs(text, keyPrefix) {
  const safe = applyInlineBold(
    text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"),
  );
  const segs = safe.split(/(<b>.*?<\/b>)/g);
  return segs.map((s, i) => {
    const m = s.match(/^<b>(.*?)<\/b>$/);
    return m ? <strong key={`${keyPrefix}-b-${i}`}>{m[1]}</strong> : <span key={`${keyPrefix}-s-${i}`}>{s}</span>;
  });
}

// Render a string that may contain markdown links, interleaved with plain
// text and inline bold. Returns an array of React nodes.
function renderInline(text, keyPrefix) {
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...renderSegs(text.slice(lastIndex, match.index), `${keyPrefix}-${key++}`));
    }
    parts.push(
      <a
        key={`${keyPrefix}-l-${key++}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#FFE7A0] underline decoration-[#FFE7A0]/50 underline-offset-2 hover:text-[#FFE7A0] transition-colors"
        data-testid="resonance-song-link"
      >
        {match[1]}
      </a>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(...renderSegs(text.slice(lastIndex), `${keyPrefix}-${key++}`));
  }
  return parts;
}

function renderParagraph(text, key) {
  return (
    <p key={key} className="mb-4">
      {renderInline(text, key)}
    </p>
  );
}

export default function ResonancePackage() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Retry-with-backoff for cross-worker eventual consistency —
      // the discernment job's `status=ready` poll just navigated us
      // here. If the GET lands on a worker that hasn't synced the
      // new disk file yet, the first call can 404. Back off through
      // several attempts before surfacing the error.
      const delays = [0, 800, 1600, 2500, 4000]; // ~9 seconds total
      let lastErrDetail = "";
      let lastErrStatus = 0;
      for (let i = 0; i < delays.length; i += 1) {
        if (cancelled) return;
        if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
        try {
          const r = await axios.get(`${API}/discernment/${id}`);
          if (!cancelled) setPkg(r.data);
          return;
        } catch (e) {
          lastErrStatus = e?.response?.status || 0;
          lastErrDetail = e?.response?.data?.detail || "Package not found";
          if (lastErrStatus && lastErrStatus !== 404) {
            if (!cancelled) setErr(lastErrDetail);
            return;
          }
        }
      }
      if (!cancelled) setErr(lastErrDetail || "Package not found");
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const downloadPdf = () => {
    const target = `${API}/discernment/${id}/pdf`;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
    if (isMobile) { window.location.href = target; return; }
    window.open(target, "_blank", "noopener");
  };

  if (err) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6" data-testid="resonance-error">
        <div className="text-center max-w-md">
          <div className="overline mb-3">Not found</div>
          <h1 className="font-serif-display text-2xl text-[#F0E6D2] mb-6">{err}</h1>
          <Link to="/discernment" className="rm-btn-ghost">New discernment</Link>
        </div>
      </main>
    );
  }

  if (!pkg) {
    return (
      <main className="min-h-screen flex items-center justify-center" data-testid="resonance-loading">
        <div className="font-cinzel text-sm tracking-[0.3em] uppercase text-[#FFE7A0]/80">Loading…</div>
      </main>
    );
  }

  return (
    <main className="relative z-10 min-h-screen flex flex-col" data-testid="resonance-page">
      <header className="relative z-20 flex items-center justify-between px-6 sm:px-12 md:px-20 py-6">
        <Link to="/" className="font-cinzel text-[0.78rem] sm:text-sm text-[#FFE7A0] font-semibold">
          THE RESTORATION MATRIX
        </Link>
        <div className="flex gap-3 items-center">
          <ReadAloudButton
            contentSelector='[data-testid="resonance-sections"]'
            testId="resonance-read-aloud-btn"
          />
          <Link to="/discernment" className="rm-btn-ghost" data-testid="resonance-new">New</Link>
        </div>
      </header>

      <section className="relative z-10 flex-1 w-full max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="overline mb-3">Discernment Packet</div>
          <h1 className="font-serif-display italic text-3xl sm:text-4xl leading-tight text-[#F0E6D2] mb-6">
            {pkg.subtitle || "Discernment"}
          </h1>

          <div className="mb-6 flex">
            <button
              onClick={downloadPdf}
              className="rm-btn rm-breathe"
              data-testid="resonance-download"
            >
              Download the PDF
            </button>
          </div>

          <div
            className="mb-10 max-w-xl border border-[#FFE7A0]/40 bg-[#FFE7A0]/5 px-5 sm:px-6 py-4"
            data-testid="resonance-ephemeral-notice"
          >
            <div className="font-cinzel text-[0.65rem] sm:text-xs tracking-[0.25em] uppercase text-[#FFE7A0] mb-2">
              Keep your package
            </div>
            <p className="text-sm sm:text-base text-[#F0E6D2]/90 font-body font-light leading-relaxed">
              This discernment exists only for this session. If you navigate
              away without downloading the PDF, it is lost forever.
            </p>
          </div>

          <div className="space-y-10" data-testid="resonance-sections">
            {(pkg.sections || []).map((sec, i) => {
              // March 2026 (user direction) — render the closing Prayer
              // section a half-step larger than the analytical sections
              // above it so the benediction lands like a final spoken
              // prayer rather than another paragraph of prose.
              const isPrayer = (sec.title || "").trim().toLowerCase() === "prayer";
              const bodySizeClass = isPrayer
                ? "text-lg sm:text-xl leading-[1.75]"
                : "text-base sm:text-lg leading-[1.7]";
              return (
              <motion.article
                key={`${sec.title}-${i}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: Math.min(i * 0.05, 0.3) }}
                data-testid={`resonance-section-${i}`}
              >
                <h2 className="font-serif-display text-2xl sm:text-3xl text-[#FFE7A0] mb-4 leading-tight">
                  {sec.title}
                </h2>
                <div className={`text-[#F0E6D2]/90 ${bodySizeClass} font-body font-light`}>
                  {(sec.body || "").split(/\n\n+/).filter((p) => p.trim()).map((para, idx) => {
                    const lines = para.split("\n").map((l) => l.trim()).filter(Boolean);
                    if (lines.length === 0) return null;
                    // Mini sub-heading if single line is a short bolded label
                    if (lines.length === 1 && /^\*\*[^*]+\*\*\s*$/.test(lines[0])) {
                      const label = lines[0].replace(/\*\*/g, "");
                      return (
                        <h3 key={idx} className="mt-6 mb-2 font-cinzel text-sm sm:text-base tracking-[0.18em] uppercase text-[#FFE7A0]/85">
                          {label}
                        </h3>
                      );
                    }
                    // Bullet list: every line begins with "- "
                    if (lines.every((l) => l.startsWith("- ")) && lines.length >= 2) {
                      return (
                        <ul key={idx} className="mt-2 mb-6 space-y-1 list-none pl-0">
                          {lines.map((l, li) => (
                            <li key={li} className="text-[#F0E6D2]/90">
                              {renderInline(l.slice(2).trim(), `${idx}-${li}`)}
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    // Block quote: all lines begin with "> "
                    if (lines.every((l) => l === ">" || l.startsWith("> "))) {
                      const cleaned = lines.map((l) => l.replace(/^>\s?/, "")).join(" ");
                      return (
                        <blockquote
                          key={idx}
                          className="mt-3 mb-4 pl-4 border-l-2 border-[#FFE7A0]/50 italic text-[#F0E6D2]/80"
                        >
                          {renderInline(cleaned, `q-${idx}`)}
                        </blockquote>
                      );
                    }
                    // Mixed: label line + quoted lines + pastoral tail
                    const quoteStart = lines.findIndex((l) => l.startsWith("> "));
                    if (quoteStart > 0) {
                      const lead = lines.slice(0, quoteStart).join(" ");
                      const quoteEnd = (() => {
                        let i = quoteStart;
                        while (i < lines.length && (lines[i] === ">" || lines[i].startsWith("> "))) i++;
                        return i;
                      })();
                      const quoteLines = lines.slice(quoteStart, quoteEnd).map((l) => l.replace(/^>\s?/, ""));
                      const tail = lines.slice(quoteEnd).join(" ");
                      return (
                        <div key={idx} className="mb-5">
                          <p className="mb-2">{renderInline(lead, `lead-${idx}`)}</p>
                          <blockquote className="mt-1 mb-2 pl-4 border-l-2 border-[#FFE7A0]/50 italic text-[#F0E6D2]/80">
                            {renderInline(quoteLines.join(" "), `q-${idx}`)}
                          </blockquote>
                          {tail && (
                            <p className="text-[#F0E6D2]/85">{renderInline(tail, `tail-${idx}`)}</p>
                          )}
                        </div>
                      );
                    }
                    const joined = lines.join(" ");
                    return renderParagraph(joined, idx);
                  })}
                </div>
              </motion.article>
              );
            })}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={downloadPdf} className="rm-btn rm-breathe" data-testid="resonance-download-bottom">
              Download the PDF
            </button>
            <Link to="/discernment" className="rm-btn-ghost" data-testid="resonance-new-bottom">
              Run another discernment
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
